import { api, endpoints } from "api/api";
import { AxiosPromise, AxiosResponse } from "axios";
import { BaseApiPaging, UserSurveyApi } from "./types";
import { InviteUserData } from "pages/CourseInviteUsers/CourseInviteUsersPage";
import { RegistrationTrancheStatus } from "consts";

const fetch = (
  courseId: number,
  skip: number,
  take: number,
  searchPhrase: string,
): AxiosPromise<BaseApiPaging<CourseStudentApi>> => {
  return api.get<BaseApiPaging<CourseStudentApi>>(endpoints.COURSES_STUDENTS.GET(courseId, skip, take, searchPhrase));
};

const fetechDocuments = (courseId: number): AxiosPromise<CourseStudentDocument[]> => {
  return api.get<CourseStudentDocument[]>(endpoints.COURSES_STUDENTS.GET_DOCUMENTS(courseId));
};

const fetchEndSurvey = (courseId: number, userId: number): AxiosPromise<UserSurveyApi> => {
  return api.get<UserSurveyApi>(endpoints.COURSES_STUDENTS.GET_END_SURVEY(courseId, userId));
};

const download = (courseId: number, courseDocumentFileId: number): AxiosPromise<AxiosResponse<Blob>> => {
  return api.get(endpoints.COURSES_STUDENTS.DOWNLOAD(courseId, courseDocumentFileId), {
    responseType: "blob",
  });
};

const zipFiles = (
  courseId: number,
  courseDocumentFileId: number,
  body: { students: number[] },
): AxiosPromise<AxiosResponse<Blob>> => {
  return api.post(endpoints.COURSES_STUDENTS.ZIP_FILES(courseId, courseDocumentFileId), body, {
    responseType: "blob",
  });
};

const zipDownload = (
  courseId: number,
  body: { courseDocumentFileIds: number[]; filename?: string },
): AxiosPromise<AxiosResponse<Blob>> => {
  return api.post(endpoints.COURSES_STUDENTS.ZIP_DOWNLOAD(courseId), body, {
    responseType: "blob",
  });
};

const pdfMerge = (
  courseId: number,
  body: { courseDocumentFileIds: number[]; filename?: string },
): AxiosPromise<AxiosResponse<Blob>> => {
  return api.post(endpoints.COURSES_STUDENTS.PDF_MERGE(courseId), body, {
    responseType: "blob",
  });
};

const invite = (
  courseId: number,
  body: { advanceCourseTrancheId: number; restCourseTrancheId: number; users: InviteUserData[] },
): AxiosPromise<void> => {
  return api.post<void>(endpoints.COURSES_STUDENTS.INVITE(courseId), body);
};

export const courseStudentsApi = {
  fetch,
  fetechDocuments,
  download,
  fetchEndSurvey,
  zipFiles,
  zipDownload,
  pdfMerge,
  invite,
};

export interface CourseStudentApi {
  avatar: string | null;
  bornDate: Date;
  bornPlace: string;
  buyer: string;
  buyerAddress: string;
  buyerNIP: string;
  cancellationRequestedAt: string | null;
  cancellationReason: string | null;
  cancellationRejectedAt: string | null;
  cancellationRejectionReason: string | null;
  discountCode: string | null;
  email: string;
  firstName: string;
  forms: {
    id: number;
    value: string;
    courseRegistrationId: number;
    courseFormId: number;
    courseFormFieldId: number;
    courseFormType: number;
    courseFormFieldName: string;
    courseFormFieldType: number;
  }[];
  fullyPaid: boolean;
  id: number;
  isInvoice: boolean;
  isUrzadPracy: boolean;
  licencePhoto: string;
  notes: UserNoteApi[];
  originalTotalPrice: number;
  pesel: string;
  price: number;
  registrationDate: Date;
  surname: string;
  tranches: CourseStudentTrancheApi[];
  urzadPracyName: string;
}

export interface UserNoteApi {
  id: number;
  notes: string;
  createdDate: Date;
  authorFullName: string;
}

export interface CourseStudentTrancheApi {
  amount: number;
  courseTrancheId: number;
  discountedAmount: number;
  dueDate: Date;
  name: string;
  paymentId: number | null;
  status: RegistrationTrancheStatus;
}

export interface CourseStudentDocument {
  id: number;
  firstName: string;
  surname: string;
  pesel: string;
  email: string;
  documents: StudentDocumentFile[];
}
export interface StudentDocumentFile {
  courseDocumentFileId: number;
  courseDocumentId: number;
  documentName: string;
  fileName: string;
  status: number;
  typeCount: number;
  errorMessage: string | null;
}
